import { 
  ArrowLeft, 
  Share2,
  MapPin, 
  Clock, 
  Camera, 
  Navigation, 
  Star, 
  ChevronLeft, 
  ChevronRight,
  Heart,
  User
} from "lucide-react"
import { QueryKeys } from "../../lib/QueryKeys";
import { useQuery } from "@tanstack/react-query";
import { 
  Button, 
  Image, 
  useRepository
} from "@city-sage/aurum-avis-labs-components";
import { Avatar } from "../Avatar";
import { Link, useParams } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { LikeButton } from "../actions/LikeButton";
import { ShareButton } from "../actions/ShareButton";
import { CommentButton } from "../actions/CommentButton";
import { useNavigationModalHandler } from "../../lib/hooks/useNavigation";
import { NavigationProviderModal } from "../actions/NavigationProviderModal";
import { useReactions } from "../../lib/useReactions";
import { formatDistanceToNow } from "date-fns";
import { ImageGallery } from "../ImageGallery";
import { StopMap } from "../create-stop/StopMap";
import CompactHeader from "../CompactHeader";
import { MarkdownRenderer } from "../MarkdownRenderer";
import { Image as ImageType } from '@city-sage/core-client';

// Post data interface for post-related images
interface PostData {
  id: string;
  account: {
    id: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  };
  createdAt: Date;
  text?: string;
}

export default function StopPage() {
  const { itineraryId, stopId } = useParams({ from: '/itineraries/$itineraryId/stops/$stopId' });
  const { api } = useRepository();
  const { t } = useTranslation();
  const navigation = useNavigationModalHandler();
  
  // Use real reactions instead of mock state
  const { 
    isLiked, 
    likeCount, 
    toggleLike,
    isLoading: isLoadingReactions,
    isPending: isReactionPending
  } = useReactions({
    reactableType: 'stop',
    reactableId: stopId
  });
  
  const { data: stop, isLoading, error } = useQuery({
    queryKey: QueryKeys.itinerary.stop(itineraryId, stopId),
    queryFn: () => api.findOneItineraryStop({ itineraryId, id: stopId }),
    enabled: !!itineraryId && !!stopId
  });

  // Fetch posts for this stop
  const { data: posts, isLoading: isLoadingPosts } = useQuery({
    queryKey: QueryKeys.post.byPostable('STOP', stopId),
    queryFn: () => api.findAllPost({ 
      filter: JSON.stringify({ 
        postableType: 'STOP', 
        postableId: stopId 
      }),
      // TODO: Remove this once we have a better way to handle this
      limit: 1000,
    }),
    enabled: !!stopId
  });

  // Create separate arrays for the ImageGallery component
  const { imageUrls, images, titles, postData } = useMemo(() => {
    if (!stop) return { imageUrls: [], images: [], titles: [], postData: [] };

    const urls: string[] = [];
    const imageObjects: (ImageType | undefined)[] = [];
    const imageTitles: (string | undefined)[] = [];
    const postDataArray: (PostData | undefined)[] = [];

    // Add stop image first if it exists
    if (stop.imageUrl) {
      urls.push(stop.imageUrl);
      imageObjects.push(stop.image || undefined);
      imageTitles.push(stop.title);
      postDataArray.push(undefined); // Not a post image
    }

    // Add post images with user info
    if (posts) {
      posts.forEach(post => {
        if (post.imageUrl) {
          urls.push(post.imageUrl);
          imageObjects.push(undefined); // Posts might not have Image objects yet
          imageTitles.push(post.text || undefined);
          postDataArray.push({
            id: post.id,
            account: post.account,
            createdAt: new Date(post.createdAt),
            text: post.text
          });
        }
      });
    }

    return {
      imageUrls: urls,
      images: imageObjects,
      titles: imageTitles,
      postData: postDataArray
    };
  }, [stop, posts]);

  if (isLoading) {
    return <div className="min-h-dvh bg-gray-50 flex items-center justify-center">
      <div>Loading...</div>
    </div>
  }

  if (error) {
    return <div className="min-h-dvh bg-gray-50 flex items-center justify-center">
      <div>Error loading stop</div>
    </div>
  }

  if (!stop) {
    return <div className="min-h-dvh bg-gray-50 flex items-center justify-center">
      <div>Stop not found</div>
    </div>
  }

  const handleNavigate = () => {
    if (stop.place.coordinates) {
      navigation.navigate({
        latitude: stop.place.coordinates.latitude,
        longitude: stop.place.coordinates.longitude,
        title: stop.title,
        address: formatAddress()
      });
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatAddress = () => {
    if (!stop.place.address) return "Location not specified";

    const parts = [
      stop.place.address.addressLine1,
      stop.place.address.addressLine2,
      [stop.place.address.city, stop.place.address.state, stop.place.address.postalCode].filter(Boolean).join(", "),
      stop.place.address.countryName,
    ].filter(Boolean);

    return parts.join(" • ");
  };

  return (
    <div className="min-h-dvh bg-gray-50">
      {/* Compact Header */}
      <CompactHeader
        backTo={`/itineraries/${itineraryId}`}
        icon={MapPin}
        title={stop?.title || 'Loading...'}
        subtitle={stop ? formatAddress() : 'Stop details'}
      />

      {/* Hero Image Section */}
      <ImageGallery 
        imageUrls={imageUrls}
        images={images}
        titles={titles}
        postData={postData}
        aspectRatio="aspect-[4/3]"
        showCounter={true}
        showDots={true}
        showArrows={true}
      />

      {/* Content */}
      <div className="-mt-6 relative z-10 pb-6 space-y-2">
        {/* Main Info Section */}
        <div className="bg-white px-6 py-6">
          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">{stop.title}</h1>

          {/* Address */}
          <div className="flex items-start gap-2 text-gray-600 mb-4">
            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-sm leading-relaxed">{formatAddress()}</span>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-center gap-6 mb-6">
            <LikeButton
              isLiked={isLiked}
              likeCount={likeCount}
              onToggleLike={toggleLike}
              variant="light"
              layout="horizontal"
              size="md"
            />
            <CommentButton
              postableType="stop"
              postableId={stopId}
              variant="light"
              layout="horizontal"
              size="md"
            />
            <ShareButton
              url={window.location.href}
              title={stop.title}
              description={stop.description || stop.highlight}
              variant="light"
              layout="horizontal"
              size="md"
            />
          </div>

          {/* Time and Duration */}
          {(stop.startTime || stop.durationMinutes) && (
            <div className="flex items-center gap-4 mb-4">
              {stop.startTime && (
                <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-xl">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">{stop.startTime}</span>
                </div>
              )}
              {stop.durationMinutes && (
                <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-xl">
                  <Clock className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-900">{formatDuration(stop.durationMinutes)}</span>
                </div>
              )}
            </div>
          )}

          {/* Navigation CTA */}
          <Button
            onClick={handleNavigate}
            size="lg"
            className="w-full"
          >
            <Navigation className="h-4 w-4 mr-2" />
            {t('itinerary.stopPage.getDirections')}
          </Button>

        </div>

        {/* Highlight Section */}
        {stop.highlight && (
          <div className="bg-white px-6 py-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500 fill-current" />
              {t('itinerary.stopPage.highlight')}
            </h2>
            <MarkdownRenderer content={stop.highlight} className="text-gray-700 leading-relaxed" />
          </div>
        )}

        {/* Description Section */}
        {stop.description && (
          <div className="bg-white px-6 py-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('itinerary.stopPage.aboutThisPlace')}</h2>
            <MarkdownRenderer content={stop.description} className="text-gray-700 leading-relaxed" />
          </div>
        )}

        {/* Location Details */}
        <div className="bg-white px-6 py-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            {t('itinerary.stopPage.locationDetails')}
          </h2>

          {/* Interactive Map */}
          <StopMap 
            stop={stop} 
            height="h-64"
            className="mb-2"
          />

          {/* Full Address */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-gray-700 leading-relaxed">{formatAddress()}</p>
                
                {/* Open in Maps Link */}
                <button
                  onClick={handleNavigate}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium mt-2 underline underline-offset-2 transition-colors"
                >
                  {t('itinerary.stopPage.openInMaps')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Provider Modal */}
      <NavigationProviderModal
        isOpen={navigation.isModalOpen}
        onClose={() => navigation.setIsModalOpen(false)}
        onSelect={navigation.handleProviderChoice}
        destinationName={stop?.title}
      />
    </div>
  );
} 